import type { Locale } from "shared-core/services/i18n.ts";
import { type Component, defineAsyncComponent } from "vue";

export type BlogFrontmatter = {
  title: string;
  header: string;
  headerImage: string;
  headerImageDimensions?: { height: number; width: number } | undefined;
  headerImageSrcset?: string | undefined;
  date: string; // ISO string
  description: string;
  category: string;
  draft: boolean;
  authorKey: string; // Should be an author which is available under the people key in translations and has an image in /images/people folder.
};

export type BlogPost = BlogFrontmatter & {
  locale: Locale;
  year: string;
  month: string;
  day: string;
  slug: string; // e.g. "my-first-post"
  routePath: string; // e.g. "/en/resources/2025/04/15/my-first-post"
  component: unknown;
};

// 1) Eager: only the named export "frontmatter"
// eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
const frontmatterByMetaFile = import.meta.glob(
  "/content/blog/*/*/*/*/*/meta.ts",
  {
    eager: true,
    import: "frontmatter",
  },
) as Record<string, BlogFrontmatter>;

// 2) Lazy: the default export (the MDX component)
const componentLoaderByMdxFile = import.meta.glob(
  "/content/blog/*/*/*/*/*/index.mdx",
  {
    eager: false,
    import: "default",
  },
) as Record<string, () => Promise<Component>>;

function parseFromMetaPath(path: string): {
  locale: Locale;
  year: string;
  month: string;
  day: string;
  slug: string;
} {
  // /content/blog/en/2025/04/15/tutorial/index.mdx
  const parts = path.split("/").filter(Boolean);
  const locale = parts[2] as Locale;
  const year = parts[3];
  const month = parts[4];
  const day = parts[5];
  const slug = parts[6];
  return { locale, year, month, day, slug };
}

function mdxPathFromMetaPath(metaPath: string): string {
  return metaPath.replace(/\\/g, "/").replace(/\/meta\.ts$/, "/index.mdx");
}

function includeDrafts(): boolean {
  return import.meta.env.DEV;
}

function isVisible(post: BlogPost): boolean {
  return includeDrafts() || !post.draft;
}

export const posts: BlogPost[] = [];

for (const [metaPath, frontmatterRaw] of Object.entries(
  frontmatterByMetaFile,
)) {
  const { locale, year, month, day, slug } = parseFromMetaPath(metaPath);

  if (!year || !month || !day || !slug) {
    console.error("Invalid meta path", metaPath);
    continue;
  }

  const mdxPath = mdxPathFromMetaPath(metaPath);
  const loader = componentLoaderByMdxFile[mdxPath];

  if (!loader) {
    console.error("Missing MDX component for meta", { metaPath, mdxPath });
    continue;
  }

  const fm = frontmatterRaw ?? ({} as BlogFrontmatter);

  if (fm.headerImage.includes(".avif") && !fm.headerImageSrcset) {
    throw new Error(".avif image must provide srcset");
  }

  posts.push({
    title: fm.title ?? slug,
    date: fm.date ?? `${year}-${month}-${day}`,
    header: fm.header,
    headerImage: fm.headerImage,
    headerImageDimensions: fm.headerImageDimensions,
    headerImageSrcset: fm.headerImageSrcset,
    description: fm.description,
    category: fm.category,
    draft: fm.draft ?? false,
    authorKey: fm.authorKey,

    locale,
    year,
    month,
    day,
    slug,
    routePath: `/${locale}/resources/${year}/${month}/${day}/${slug}`,
    component: defineAsyncComponent(() => loader()),
  });
}

export function getPost(
  locale: Locale,
  year: string,
  month: string,
  day: string,
  slug: string,
): BlogPost | null {
  return (
    posts.find(
      (p) =>
        p.locale === locale &&
        p.year === year &&
        p.month === month &&
        p.day === day &&
        p.slug === slug &&
        isVisible(p),
    ) ?? null
  );
}

export function getThreeClosePosts(
  locale: Locale,
  year: string,
  month: string,
  day: string,
  slug: string,
): BlogPost[] {
  const postsWanted: number = 3;
  const sameYearPosts: BlogPost[] = [];
  const sameMonthPosts: BlogPost[] = [];
  const sameDayPosts: BlogPost[] = [];

  for (const post of posts) {
    if (
      post.locale === locale &&
      (!post.draft || import.meta.env.DEV) &&
      !(
        post.locale === locale &&
        post.year === year &&
        post.month === month &&
        post.day === day &&
        post.slug === slug
      )
    ) {
      if (post.year === year) {
        if (post.month === month) {
          if (post.day === day) {
            sameDayPosts.push(post);

            continue;
          }

          sameMonthPosts.push(post);

          continue;
        }

        sameYearPosts.push(post);
      }
    }
  }

  // If we don't have enough posts from today, expand to same month
  if (sameDayPosts.length < postsWanted) {
    sameDayPosts.push(...sameMonthPosts);

    // If we don't have enough posts from this month, expand to same year
    if (sameDayPosts.length < postsWanted) {
      sameDayPosts.push(...sameYearPosts);

      // If we still don't have enough posts, include all posts of same locale and visible
      if (sameDayPosts.length < postsWanted) {
        sameDayPosts.push(
          ...posts.filter(
            (post: BlogPost): boolean =>
              post.locale === locale && isVisible(post),
          ),
        );
      }
    }
  }

  return sameDayPosts.slice(0, postsWanted);
}

export function getAllPostsForLocale(locale: Locale): BlogPost[] {
  const filteredPosts: BlogPost[] = [];

  for (const post of posts) {
    if (post.locale === locale && isVisible(post)) {
      filteredPosts.push(post);
    }
  }

  return filteredPosts;
}
